import { create } from 'zustand';
import * as THREE from 'three';
import { getOrigin, getSectionBounds } from '../config/siteConfig';
import {
  azimuthFromPoints,
  clamp,
  defaultSectionState,
  hydrateSection,
  obliqueNormalFromPoints,
  scenePointToMine,
  serializeSection,
  stepSection,
  wrapAzimuth,
} from '../Utils/sectionMath';
import { clipObjectLabel, findClipMesh } from '../Utils/objectClip';

function mineFromScene(point) {
  return scenePointToMine(point, getOrigin());
}

export const useSectionStore = create((set, get) => ({
  ...defaultSectionState(),

  setEnabled: (enabled) => set({ enabled: Boolean(enabled) }),
  setPanelOpen: (panelOpen) =>
    set({
      panelOpen: Boolean(panelOpen),
      placeMode: panelOpen ? get().placeMode : 'idle',
      pendingPoints: panelOpen ? get().pendingPoints : [],
    }),
  exitSection: () =>
    set({
      enabled: false,
      panelOpen: false,
      placeMode: 'idle',
      pendingPoints: [],
    }),
  togglePanel: () => {
    const { panelOpen, enabled } = get();
    if (panelOpen || enabled) {
      get().exitSection();
    } else {
      set({ panelOpen: true, enabled: true });
    }
  },
  setOrientation: (orientation) => {
    const cur = get();
    const next = { orientation };
    if (orientation === 'azimuth') {
      if (cur.orientation === 'east') next.azimuth = 0;
      else if (cur.orientation === 'north') next.azimuth = 90;
    }
    if (orientation !== 'fence') next.placeMode = cur.placeMode === 'fence' ? 'idle' : cur.placeMode;
    if (orientation === 'object' && !cur.clipParentUuid) {
      next.placeMode = 'object';
      next.enabled = true;
    }
    set(next);
  },
  setCenter: (partial) => {
    const bounds = getSectionBounds();
    const next = {};
    if (partial.east != null) next.centerEast = clamp(partial.east, bounds.east[0], bounds.east[1]);
    if (partial.north != null) next.centerNorth = clamp(partial.north, bounds.north[0], bounds.north[1]);
    if (partial.rl != null) next.centerRl = clamp(partial.rl, bounds.rl[0], bounds.rl[1]);
    set(next);
  },
  setWidth: (width) => set({ width: Math.max(0.1, Number(width) || 0.1) }),
  setAzimuth: (azimuth) => set({ azimuth: wrapAzimuth(azimuth) }),
  setClipMode: (clipMode) => set({ clipMode: clipMode === 'single' ? 'single' : 'corridor' }),
  setLookLocked: (lookLocked) => set({ lookLocked: Boolean(lookLocked) }),
  toggleLookLocked: () => set({ lookLocked: !get().lookLocked }),
  setLookFlip: (lookFlip) => set({ lookFlip: Boolean(lookFlip) }),
  toggleLookFlip: () => set({ lookFlip: !get().lookFlip }),
  setBoxWidth: (partial) => {
    const next = {};
    if (partial.east != null) next.boxWidthEast = Math.max(0.1, Number(partial.east) || 0.1);
    if (partial.north != null) next.boxWidthNorth = Math.max(0.1, Number(partial.north) || 0.1);
    if (partial.rl != null) next.boxWidthRl = Math.max(0.1, Number(partial.rl) || 0.1);
    set(next);
  },
  setClipHoles: (clipHoles) => set({ clipHoles: Boolean(clipHoles) }),
  setClipSolids: (clipSolids) => set({ clipSolids: Boolean(clipSolids) }),
  setShowCaps: (showCaps) => set({ showCaps: Boolean(showCaps) }),
  setShowGizmo: (showGizmo) => set({ showGizmo: Boolean(showGizmo) }),
  setStepSize: (stepSize) => set({ stepSize: Math.max(0.1, Number(stepSize) || 0.1) }),
  setPlaceMode: (placeMode) =>
    set({
      placeMode: placeMode || 'idle',
      pendingPoints: [],
      ...(placeMode === 'object' ? { enabled: true, orientation: 'object' } : {}),
    }),
  step: (dir) => set(stepSection(get(), dir, getOrigin())),
  snapshot: () => serializeSection(get()),
  hydrate: (snap) => set(hydrateSection(snap, getOrigin())),

  addPlacePoint: (scenePoint) => {
    const mine = mineFromScene(scenePoint);
    if (!mine) return;
    const { placeMode, pendingPoints } = get();
    if (placeMode === 'object') return;
    if (!placeMode || placeMode === 'idle') {
      get().setCenter(mine);
      return;
    }
    const nextPts = [...(pendingPoints || []), mine];
    if (placeMode === 'click') {
      const bounds = getSectionBounds();
      set({
        pendingPoints: [],
        placeMode: 'idle',
        enabled: true,
        centerEast: clamp(mine.east, bounds.east[0], bounds.east[1]),
        centerNorth: clamp(mine.north, bounds.north[0], bounds.north[1]),
        centerRl: clamp(mine.rl, bounds.rl[0], bounds.rl[1]),
      });
      return;
    }
    if (placeMode === 'twoPoint') {
      if (nextPts.length < 2) {
        set({ pendingPoints: nextPts });
        return;
      }
      const [a, b] = nextPts;
      set({
        pendingPoints: [],
        placeMode: 'idle',
        enabled: true,
        orientation: 'azimuth',
        azimuth: azimuthFromPoints(a, b),
        centerEast: (a.east + b.east) / 2,
        centerNorth: (a.north + b.north) / 2,
        centerRl: (a.rl + b.rl) / 2,
      });
      return;
    }
    if (placeMode === 'threePoint') {
      if (nextPts.length < 3) {
        set({ pendingPoints: nextPts });
        return;
      }
      const [a, b, c] = nextPts;
      const origin = getOrigin();
      const pa = new THREE.Vector3(a.east - origin.east, a.rl - origin.rl, origin.north - a.north);
      const pb = new THREE.Vector3(b.east - origin.east, b.rl - origin.rl, origin.north - b.north);
      const pc = new THREE.Vector3(c.east - origin.east, c.rl - origin.rl, origin.north - c.north);
      const n = obliqueNormalFromPoints(pa, pb, pc);
      set({
        pendingPoints: [],
        placeMode: 'idle',
        enabled: true,
        orientation: n ? 'oblique' : 'azimuth',
        obliqueNormal: n ? { x: n.x, y: n.y, z: n.z } : null,
        azimuth: n ? get().azimuth : azimuthFromPoints(a, b),
        centerEast: (a.east + b.east + c.east) / 3,
        centerNorth: (a.north + b.north + c.north) / 3,
        centerRl: (a.rl + b.rl + c.rl) / 3,
      });
      return;
    }
    if (placeMode === 'fence') {
      set({ pendingPoints: nextPts, enabled: true });
    }
  },

  finishFence: () => {
    const pts = get().pendingPoints || [];
    if (pts.length < 2) {
      set({ placeMode: 'idle' });
      return;
    }
    set({
      placeMode: 'idle',
      pendingPoints: [],
      fencePoints: pts,
      fenceDistance: 0,
      orientation: 'fence',
      enabled: true,
      centerEast: pts[0].east,
      centerNorth: pts[0].north,
      centerRl: pts[0].rl,
      azimuth: azimuthFromPoints(pts[0], pts[1]),
    });
  },

  cancelPlace: () => set({ placeMode: 'idle', pendingPoints: [] }),

  setClipParentMode: (clipParentMode) => {
    const mode = ['inside', 'outside', 'above', 'below'].includes(clipParentMode)
      ? clipParentMode
      : 'inside';
    set({ clipParentMode: mode, enabled: true });
  },
  setClipParentFromObject: (object) => {
    const mesh = findClipMesh(object);
    if (!mesh) return;
    set({
      clipParentUuid: mesh.uuid,
      clipParentName: clipObjectLabel(mesh),
      orientation: 'object',
      enabled: true,
      placeMode: 'idle',
    });
  },
  clearClipParent: () => {
    const { orientation } = get();
    set({
      clipParentUuid: null,
      clipParentName: '',
      clipParentClosed: false,
      clipParentUsedBox: true,
      orientation: orientation === 'object' ? 'rl' : orientation,
      placeMode: 'idle',
    });
  },

  clearPoints: () => {
    const { orientation } = get();
    set({
      placeMode: 'idle',
      pendingPoints: [],
      fencePoints: [],
      fenceDistance: 0,
      orientation: orientation === 'fence' ? 'azimuth' : orientation,
    });
  },

  handleEscape: () => {
    const { placeMode, panelOpen, enabled } = get();
    const active = Boolean((placeMode && placeMode !== 'idle') || panelOpen || enabled);
    if (active) {
      get().exitSection();
      return true;
    }
    return false;
  },
}));
